import { useEffect, useRef, useCallback } from 'react';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { foldGutter, bracketMatching } from '@codemirror/language';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { createFluentTheme, createJsonSchemaLinter, createJsonAutocomplete, createNodeHighlightExtension, setNodeHighlight, formatJsonKeymap, wrapSelectionInputHandler } from './extensions';
import type { UseCodeMirrorOptions, UseCodeMirrorReturn, Theme, ActiveFile, NodeHighlightRange } from './types';

/**
 * React hook for managing a CodeMirror 6 editor instance.
 * Handles lifecycle, theming, linting, and content synchronization.
 */
export const useCodeMirror = (options: UseCodeMirrorOptions): UseCodeMirrorReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  const lintCompartment = useRef(new Compartment());
  const autocompleteCompartment = useRef(new Compartment());
  const ariaLabelCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());
  const isUpdatingRef = useRef(false);

  // Store options in refs to access in callbacks without causing re-renders
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Create EditorView on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const { initialValue, theme, activeFile, onChange, readOnly = false } = optionsRef.current;

    const extensions: Extension[] = [
      // Core features
      lineNumbers(),
      history(),
      foldGutter({
        openText: '▼',
        closedText: '▶',
      }),
      bracketMatching(),
      wrapSelectionInputHandler(),
      closeBrackets(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),

      // JSON language support
      json(),

      // Tab size
      EditorState.tabSize.of(2),

      // Keybindings
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        ...formatJsonKeymap,
        indentWithTab,
      ]),

      // Dynamic compartments for runtime reconfiguration
      themeCompartment.current.of(createFluentTheme(theme)),
      lintCompartment.current.of(createJsonSchemaLinter(activeFile)),
      autocompleteCompartment.current.of(createJsonAutocomplete(activeFile)),

      // Node highlight extension
      createNodeHighlightExtension(),

      // Accessible label derived from active file
      ariaLabelCompartment.current.of(
        EditorView.contentAttributes.of({ 'aria-label': `${activeFile} code editor` })
      ),

      // Read-only state — toggled at runtime via the compartment
      readOnlyCompartment.current.of([
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
      ]),

      // Update listener for content changes
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isUpdatingRef.current) {
          onChange(update.state.doc.toString());
        }
      }),
    ];

    const state = EditorState.create({
      doc: initialValue,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  /**
   * Programmatically set the editor content.
   * Used for syncing with external state changes.
   *
   * Preserves scroll position and selection across full-document replacements
   * so that designer-driven updates (e.g., dragging a node) don't yank the
   * user back to the top of the JSON view.
   */
  const setValue = useCallback((value: string) => {
    if (!viewRef.current) return;
    const view = viewRef.current;

    const currentValue = view.state.doc.toString();
    if (currentValue === value) return;

    // Capture viewport + selection before the replacement so we can restore them.
    const scrollTop = view.scrollDOM.scrollTop;
    const scrollLeft = view.scrollDOM.scrollLeft;
    const previousMain = view.state.selection.main;
    const newDocLength = value.length;
    // Clamp the previous selection to the bounds of the new document so the
    // editor doesn't throw if the doc shrunk.
    const clampedAnchor = Math.min(previousMain.anchor, newDocLength);
    const clampedHead = Math.min(previousMain.head, newDocLength);

    isUpdatingRef.current = true;
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: value,
      },
      selection: { anchor: clampedAnchor, head: clampedHead },
      // Tell CodeMirror not to scroll the (clamped) selection into view —
      // we'll restore the user's scroll position immediately after.
      scrollIntoView: false,
    });
    isUpdatingRef.current = false;

    // Restore scroll synchronously after the dispatch completes its DOM updates.
    view.scrollDOM.scrollTop = scrollTop;
    view.scrollDOM.scrollLeft = scrollLeft;
  }, []);

  /**
   * Update the editor theme.
   * Used when the app theme (light/dark) changes.
   */
  const updateTheme = useCallback((theme: Theme) => {
    if (!viewRef.current) return;

    viewRef.current.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, []);

  /**
   * Update the linter for a different file type.
   * Used when switching between workflow.json and connections.json.
   */
  const updateLinter = useCallback((activeFile: ActiveFile) => {
    if (!viewRef.current) return;

    viewRef.current.dispatch({
      effects: [
        lintCompartment.current.reconfigure(createJsonSchemaLinter(activeFile)),
        autocompleteCompartment.current.reconfigure(createJsonAutocomplete(activeFile)),
        ariaLabelCompartment.current.reconfigure(
          EditorView.contentAttributes.of({ 'aria-label': `${activeFile} code editor` })
        ),
      ],
    });
  }, []);

  /**
   * Scroll to a specific line number.
   * Used for navigating to selected node's location.
   */
  const scrollToLine = useCallback((lineNumber: number) => {
    if (!viewRef.current) return;

    const doc = viewRef.current.state.doc;
    if (lineNumber < 1 || lineNumber > doc.lines) return;

    const line = doc.line(lineNumber);
    viewRef.current.dispatch({
      effects: EditorView.scrollIntoView(line.from, { y: 'center' }),
    });
  }, []);

  /**
   * Set editor selection to a character range.
   * Used for highlighting the selected node's JSON.
   */
  const setSelection = useCallback((from: number, to: number) => {
    if (!viewRef.current) return;

    viewRef.current.dispatch({
      selection: { anchor: from, head: to },
      scrollIntoView: true,
    });
    // Focus the editor to show the selection
    viewRef.current.focus();
  }, []);

  /**
   * Highlight a node in the editor with decorations.
   * Highlights the node name and adds a background to the block lines.
   */
  const highlightNode = useCallback((range: NodeHighlightRange | null) => {
    if (!viewRef.current) return;

    viewRef.current.dispatch({
      effects: setNodeHighlight.of(range),
    });

    // If we have a range, scroll to the node name and place cursor there
    if (range) {
      viewRef.current.dispatch({
        selection: { anchor: range.nameFrom },
        effects: EditorView.scrollIntoView(range.nameFrom, { y: 'center' }),
      });
    }
  }, []);

  /**
   * Toggle the editor between read-only and editable.
   * Used when entering/leaving the published (read-only) view.
   */
  const updateReadOnly = useCallback((readOnly: boolean) => {
    if (!viewRef.current) return;

    viewRef.current.dispatch({
      effects: readOnlyCompartment.current.reconfigure([
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
      ]),
    });
  }, []);

  return {
    containerRef,
    setValue,
    updateTheme,
    updateLinter,
    updateReadOnly,
    scrollToLine,
    setSelection,
    highlightNode,
  };
};
